// Company-specific stage templates for Opportunities + Transactions.
//
// AC-NAME PARITY REWRITE (Grant, 2026-07-11): every default stage list
// below now matches the ActiveCampaign stage names the team has used
// for years (minus instructional wording like "(no automation)"), so
// every board reads familiar at cutover. Slugs are slugify(label) —
// the SAME slugs the AC migration wrote onto opportunities.stage /
// transactions.stage — so migrated records land in the right columns
// with no data rewrite. Where a record-borne slug predates this rename
// (e.g. planning's `health_*`-prefixed slugs, financial's `cea_sent`),
// the template keeps the RECORD's slug and shows the AC label; each
// such case carries a DECISION comment.
//
// Schema decision (per the plan): config file in shared/db/ for v1.
// This file is the DEFAULT + the seed: as of migration 0166 the
// `stage_templates` table (shared/db/stage-templates-store.ts) is an
// editable OVERLAY on top of it, so the domain owners (Brandon for
// Insurance, Joey for Mortgage, Mike for Private Money, Ryan for
// Planning, Nate + Mark for Realty, Brian R + Jake for Financial) can
// edit their pipelines without a deploy. Code paths that must honor an
// edit call `resolveStageTemplate(db, key)` (the DB-aware resolver) —
// `lookupStageTemplate` below stays the pure, synchronous config default
// and the ultimate fallback when no override exists.
//
// Lookup order (most specific → least):
//   1. (company_slug, workflow, opportunity_type / transaction_type)
//   2. (company_slug, workflow, '*')          — company-wide default
//   3. ('*', workflow, '*')                    — global fallback
//
// Two rules on "typical_days" (feeds the stalled-detection heuristic —
// days_in_stage > typical_days × 1.5 → is_stalled):
//
//   * typical_days = 0 disables stalling for that stage. Terminal /
//     parking stages (Stale, Closed - Compliance, long-term Holding
//     tails) carry 0.
//   * The old "last Opportunity stage is always a `decision` stage"
//     rule is retired — AC's funnels don't end on a synthetic decision
//     column, and outcome (won/lost) remains an outcome transition,
//     never a stage.

import type { StageDef, StageTemplate } from "./detail-queries";
import { RENEWAL_STAGES } from "../renewals/stages";
import { titleCaseSlug } from "./service-type-labels";

export type Workflow = "opportunity" | "transaction" | "ongoing_care";

export interface StageTemplateKey {
  /** Company slug — 'insurance', 'mortgage', 'financial', 'planning', 'realty'. */
  companySlug: string;
  workflow: Workflow;
  /** opportunity_type or transaction_type, or '*' for the company default. */
  type: string;
}

// ─── Insurance templates ──────────────────────────────────────────
//
// Brandon Ream's AC quoting funnel, verbatim. Joy Smith's renewal
// cycle is a Calendar Rhythm (Ongoing Care) with its own catalog
// (shared/renewals/stages.ts) — it doesn't appear here.
//
// Note: 1,828 migrated won quotes sit at `issued_policy` (AC's
// terminal stage). It's deliberately NOT a column — those records are
// closed (outcome=won) and terminal state is an outcome, not a stage.

const INSURANCE_QUOTE_STAGES: StageDef[] = [
  { slug: "need_to_contact", label: "Need To Contact", typical_days: 2, sort_order: 1 },
  { slug: "quoting", label: "Quoting", typical_days: 3, sort_order: 2 },
  { slug: "quotes_review", label: "Quotes Review", typical_days: 3, sort_order: 3 },
  { slug: "quotes_delivered", label: "Quotes Delivered", typical_days: 7, sort_order: 4 },
  // DECISION (Brandon, 2026-08-04): "Quotes Delivered Follow Up" dropped —
  // he doesn't work the board through that stage. The opportunity-stage-
  // retire data pass moves the handful of historical (closed-lost) records
  // that carried the slug onto Quotes Delivered so nothing is orphaned.
  //
  // DECISION (Brandon, 2026-08-06): "Quotes Accepted Waiting" moved OFF
  // this board and onto Insurance Processing (see INSURANCE_POLICY_ISSUANCE
  // below) — it's the entry stage where a won quote lands by default and
  // Joy issues the policy in the carrier system, not a place a still-open
  // Opportunity waits. Final Ask is now the last column; the trailing-
  // column rule puts typical_days 0 here.
  { slug: "final_ask", label: "Final Ask", typical_days: 0, sort_order: 5 },
];

// DECISION (Brandon, 2026-08-06): rebuilt around the actual carrier-issuance
// + document-review workflow he and Mylah run, replacing the old
// Bound/Paid/Issued/In force columns those two never worked the board
// through. "Won" is deliberately NOT a column — Brandon's own framing: at
// that point the household is a client, the outcome transition (the
// existing "Mark won" completion button) flips the service record to
// active and spawns the Ongoing Care renewal cycle. See
// shared/transactions/outcomes.ts's completionVerb().
//
// `quotes_accepted_waiting` reuses the slug the Opportunity board used to
// carry for this same moment ("they said yes") — a won Insurance quote
// lands here automatically because it's the board's first column
// (opportunity-transitions.ts resolves the entry stage from the template).
//
// DECISION: label is "Policy Issued" rather than Brandon's verbatim
// "Issued Policy" — slugify("Issued Policy") would be `issued_policy`,
// which already means something specific and unrelated elsewhere in the
// schema (the AC-migration terminal marker on `opportunities.stage`,
// documented above as "deliberately NOT a column"). Reordering the words
// keeps the slug/label parity the drift guard below requires without
// reusing that string for a second, different meaning.
const INSURANCE_POLICY_ISSUANCE: StageDef[] = [
  {
    slug: "quotes_accepted_waiting",
    label: "Quotes Accepted Waiting",
    typical_days: 2,
    sort_order: 1,
  },
  { slug: "policy_issued", label: "Policy Issued", typical_days: 3, sort_order: 2 },
  { slug: "document_review", label: "Document Review", typical_days: 5, sort_order: 3 },
];

// ─── Mortgage templates ───────────────────────────────────────────
//
// Joey Ellis owns Traditional; Mike Zins owns Private Money. AC ran
// Traditional as two pipelines (Need To Contact + Pre Qualify) — the
// one OS board carries the union of their stages, per Grant.

// Column order follows AC's two boards read left to right — Need To
// Contact's four columns, then Pre Qualify's nine (Grant's 2026-07-31
// call: "merge both pipelines into one for the OS").
const MORTGAGE_TRAD_OPPORTUNITY: StageDef[] = [
  { slug: "need_to_contact", label: "Need To Contact", typical_days: 2, sort_order: 1 },
  { slug: "appointment_scheduled", label: "Appointment Scheduled", typical_days: 5, sort_order: 2 },
  { slug: "evaluating", label: "Evaluating", typical_days: 5, sort_order: 3 },
  { slug: "hopper", label: "Hopper", typical_days: 14, sort_order: 4 },
  { slug: "refinance_analysis", label: "Refinance Analysis", typical_days: 7, sort_order: 5 },
  { slug: "pre_qualify", label: "Pre Qualify", typical_days: 7, sort_order: 6 },
  { slug: "sent_sonar_link_waiting", label: "Sent Sonar Link - Waiting", typical_days: 14, sort_order: 7 },
  { slug: "pre_approval", label: "Pre Approval", typical_days: 14, sort_order: 8 },
  { slug: "holding_3_months", label: "Holding 3 Months", typical_days: 90, sort_order: 9 },
  { slug: "holding_12_months", label: "Holding 12 Months", typical_days: 0, sort_order: 10 },
  { slug: "holding_12_months_extended", label: "Holding 12 Months Extended", typical_days: 0, sort_order: 11 },
  { slug: "refinance_analysis_holding", label: "Refinance Analysis Holding", typical_days: 30, sort_order: 12 },
  // DECISION: slug is the record-borne `stale_no_response` (AC's full
  // stage name was "Stale (No Response)"); Grant's label strips the
  // parenthetical. slugify("Stale") would orphan the migrated rows.
  { slug: "stale_no_response", label: "Stale", typical_days: 0, sort_order: 13 },
];

// AC's "Registered" board per the 2026-07-31 screenshot, with ONE
// deliberate deviation: AC displays Moving Forward leftmost, but in
// the OS the first column is the ENTRY stage — where a newly-won loan
// lands — and Joey's call is that new files start at Initially
// Submitted (a just-won loan showing as already "moving" would be
// wrong). AC's leftmost column was cosmetic; ours is behavior.
// "Closing Docs Signed" was invisible to the deals export during the
// 07-11 parity pass because it held no deals then; it holds live
// files now.
const MORTGAGE_TRAD_PROCESSING: StageDef[] = [
  { slug: "initially_submitted", label: "Initially Submitted", typical_days: 3, sort_order: 1 },
  { slug: "moving_forward", label: "Moving Forward", typical_days: 5, sort_order: 2 },
  { slug: "conditionally_approved", label: "Conditionally Approved", typical_days: 7, sort_order: 3 },
  { slug: "clear_to_close", label: "Clear to Close", typical_days: 3, sort_order: 4 },
  { slug: "closing_docs_signed", label: "Closing Docs Signed", typical_days: 2, sort_order: 5 },
  { slug: "funded", label: "Funded", typical_days: 5, sort_order: 6 },
  { slug: "closed_compliance", label: "Closed - Compliance", typical_days: 0, sort_order: 7 },
  { slug: "new_build_waiting", label: "New Build Waiting", typical_days: 0, sort_order: 8 },
];

// Mike Zins's own six columns, given in his 2026-09-11 review of the
// Private Money loan-desk mockup and approved by Grant 2026-09-17
// (docs/design/private-money-loan-desk.md §5.1). This replaces the
// four-column AC-parity list, which had no home for the two moments
// Mike actually works: a borrower who has signed the loan application
// and is now in underwriting, and a borrower who is real but not ready
// yet.
//
// Slug notes, because two of these have to line up with rows that
// already exist:
//   * `quoting` replaces AC's `quoted`. Mike's word is the ACTIVE one —
//     he is quoting the deal, not filing a finished quote — and the
//     existing rows carrying `quoted` are moved by the stage-remap data
//     pass, never by this file.
//   * `stale` is unchanged and keeps its slug and its position LAST.
//     On the AC board it sat oddly in the middle; Mike's list ends with
//     it, which is also where a parking column belongs.
//   * `loan_application` and `nurture` are new and start empty.
//
// typical_days feeds stalled-detection only (days_in_stage >
// typical_days x 1.5). Nurture and Stale are parking columns: Nurture
// gets a long fuse rather than 0 because a borrower parked there IS
// meant to be picked back up, and Stale gets 0 because it is where work
// goes to stop.
const MORTGAGE_PM_OPPORTUNITY: StageDef[] = [
  { slug: "need_to_contact", label: "Need To Contact", typical_days: 2, sort_order: 1 },
  { slug: "evaluating", label: "Evaluating", typical_days: 3, sort_order: 2 },
  { slug: "quoting", label: "Quoting", typical_days: 3, sort_order: 3 },
  { slug: "loan_application", label: "Loan Application", typical_days: 7, sort_order: 4 },
  { slug: "nurture", label: "Nurture", typical_days: 45, sort_order: 5 },
  { slug: "stale", label: "Stale", typical_days: 0, sort_order: 6 },
];

// Private Money Processing — Mike's three columns (same review, same
// approval). Supersedes the single "Processing" column (Grant,
// 2026-07-11), which was an honest placeholder: one column cannot say
// whether a file is waiting on a human to pick it up or waiting on a
// title commitment, and Mike's board is where that distinction lives.
//
// There is deliberately NO Terminated column, though Mortgage Automator
// has one. Mike, 2026-09-04: Terminated exists over there only because
// it is the trigger for a required compliance document — the Notice of
// Action Taken — whenever a signed loan application does not close.
// *"We may not even need Terminated as a stage, but just as part of that
// outcome of a lost deal that has a signed loan application."* So the
// document is raised on the lost/fell-through OUTCOME instead
// (shared/db/transaction-conditions.ts, addNoticeOfActionCondition) and
// there is no Terminated concept anywhere in the OS.
//
// AC's "Closed" likewise maps to the completed outcome, not a column:
// the 620 migrated `closed` rows are already outcome=completed and never
// render on the open board.
const MORTGAGE_PM_PROCESSING: StageDef[] = [
  { slug: "new_loan_request", label: "New Loan Request", typical_days: 2, sort_order: 1 },
  { slug: "conditions", label: "Conditions", typical_days: 14, sort_order: 2 },
  // Terminal: the loan has funded and compliance is closing the file.
  // Nothing stalls here, and this is where the PM loan record is created
  // (shared/service-records/product-stage.ts).
  { slug: "closed_compliance", label: "Closed - Compliance", typical_days: 0, sort_order: 3 },
];

// ─── Financial templates ──────────────────────────────────────────
//
// Stewardship Financial's pipeline (Brian Ream / Jake). AC names plus
// Brian's two OS-authored extras (Need to Follow Up (Hot Lead), No
// Further Contact Needed) — kept per Grant 2026-07-11.

const FINANCIAL_OPPORTUNITY: StageDef[] = [
  { slug: "need_to_contact", label: "Need to Contact", typical_days: 1, sort_order: 1 },
  { slug: "contact_made", label: "Contact Made", typical_days: 3, sort_order: 2 },
  { slug: "appointment_scheduled", label: "Appointment Scheduled", typical_days: 5, sort_order: 3 },
  { slug: "post_appointment", label: "Post Appointment", typical_days: 3, sort_order: 4 },
  // DECISION: Brian's two OS-authored extras keep their pre-parity
  // slugs (`follow_up_hot`, `no_further_contact`) — they're referenced
  // by the judgment catalog's late-stage sets and re-slugifying buys
  // nothing (no AC records carry either).
  { slug: "follow_up_hot", label: "Need to Follow Up (Hot Lead)", typical_days: 7, sort_order: 5 },
  { slug: "nurture", label: "Nurture", typical_days: 45, sort_order: 6 },
  { slug: "stale", label: "Stale", typical_days: 0, sort_order: 7 },
  // Terminal (cold / unfit). typical_days 0 — exits the pipeline.
  { slug: "no_further_contact", label: "No Further Contact Needed", typical_days: 0, sort_order: 8 },
];

// FINANCIAL: Membership — the fee-based planning membership funnel.
// New pipeline type (no migrated rows carry it: the AC Membership
// deals were consolidated into financial_needs_assessment during the
// 2026-06 dedupe); exists so new membership work gets its own board.
// AC's Membership board with ONE deliberate deviation: AC displays
// Onboarding leftmost, but the OS's first column is the ENTRY stage,
// and a brand-new membership prospect starts at Schedule 1st Meeting,
// not Onboarding. The renewal tail keeps AC's true order (Renewal
// Meeting = "send the schedule-meeting email", THEN Renewal Meeting
// Scheduled), and gains "Renewal Payment" — an empty column the deals
// export couldn't see.
const FINANCIAL_MEMBERSHIP: StageDef[] = [
  { slug: "schedule_1st_meeting", label: "Schedule 1st Meeting", typical_days: 5, sort_order: 1 },
  { slug: "1st_meeting_scheduled", label: "1st Meeting Scheduled", typical_days: 7, sort_order: 2 },
  { slug: "2nd_meeting_scheduled", label: "2nd Meeting Scheduled", typical_days: 7, sort_order: 3 },
  { slug: "onboarding", label: "Onboarding", typical_days: 14, sort_order: 4 },
  { slug: "renewal_hopper", label: "Renewal Hopper", typical_days: 0, sort_order: 5 },
  { slug: "renewal_meeting", label: "Renewal Meeting", typical_days: 7, sort_order: 6 },
  { slug: "renewal_meeting_scheduled", label: "Renewal Meeting Scheduled", typical_days: 7, sort_order: 7 },
  { slug: "renewal_payment", label: "Renewal Payment", typical_days: 0, sort_order: 8 },
];

// AC's "Investments" board. The 07-11 pass had Appointment Scheduled
// FIRST; the screenshot shows it fourth — assets arrive, THEN the
// appointment is booked. Real correction to Jake's flow, not a
// cosmetic reorder.
const FINANCIAL_ONBOARDING: StageDef[] = [
  // DECISION: slug stays the record-borne `cea_sent` (Grant + Brian
  // Ream's 2026-06-26 stage-cleanup call mapped AC's "Onboarding"
  // stage to it); the label is AC's "Onboarding" per the 2026-07-11
  // parity decision. Re-slugifying would orphan 54 migrated rows.
  { slug: "cea_sent", label: "Onboarding", typical_days: 7, sort_order: 1 },
  { slug: "waiting_on_assets", label: "Waiting on Assets", typical_days: 7, sort_order: 2 },
  // DECISION: record-borne `assets_received` slug keeps AC's fuller
  // label ("- Needs Appointment" is the actual next action).
  { slug: "assets_received", label: "Assets Received - Needs Appointment", typical_days: 3, sort_order: 3 },
  { slug: "appointment_scheduled", label: "Appointment Scheduled", typical_days: 5, sort_order: 4 },
  // Terminal — compliance review, then the household moves to the
  // Current Clients (Ongoing Care) lane.
  { slug: "post_appointment", label: "Post Appointment", typical_days: 0, sort_order: 5 },
];

// ─── Planning templates ───────────────────────────────────────────
//
// Ryan Delviken's health / life-DI / annuity / estate workflows, AC
// names. DECISION: the AC migration slugified planning stage labels
// WITH their pipeline prefix ("Health - Need To Contact" →
// `health_need_to_contact`, "Life/DI - …" → `life_di_*`, "Annuity - …"
// → `annuity_*`), so those templates keep the record-borne prefixed
// slugs while the labels show the clean AC stage names.

const PLANNING_HEALTH_CONTACT: StageDef[] = [
  { slug: "health_need_to_contact", label: "Need To Contact", typical_days: 2, sort_order: 1 },
  { slug: "health_contact_made", label: "Contact Made", typical_days: 3, sort_order: 2 },
  { slug: "health_contact_made_need_more_info", label: "Contact Made & Need More Info", typical_days: 5, sort_order: 3 },
  // Appointment Scheduled precedes Holding on AC's board (07-31
  // screenshot); the 07-11 pass had them reversed.
  { slug: "health_appointment_scheduled", label: "Appointment Scheduled", typical_days: 5, sort_order: 4 },
  { slug: "health_holding", label: "Holding", typical_days: 0, sort_order: 5 },
];

// Health quoting — the AC "PLANNING: Health - Quoting" pipeline. Its
// migrated rows carry opportunity_type `insurance_quote` (under the
// planning company), a migration-era heuristic artifact; the template
// is keyed to that REAL record type. Life/DI - Quoting rows share the
// same type and surface here too (see PR notes).
// DECISION: two labels restored to AC's exact wording (the 07-11 pass
// paraphrased them). Slugs stay record-borne so migrated rows keep
// their columns: `conditionally_approved` shows "Need Forms or
// Conditionally Approved", `awaiting_payment` shows "Need Payment".
//
// QUOTES SENT (Ryan, 2026-08-25 — program session S6,
// ryan-planning-views.md §5). Ryan's ask: "are they responding?" needs
// a column that means "I sent the quotes and I'm waiting on them", so the
// 3-day follow-up flag has something to count. AC's board went straight
// from Quoting to the responses (Need Forms / Need Payment), so the wait
// itself was invisible — a quote sent on Monday and a quote sent three
// weeks ago both read as "Quoting".
//
// This board is the one that genuinely lacked it, and it carries ACA,
// Short-Term AND Medicare quoting (all three ride opportunity_type
// `insurance_quote` under Planning), so adding it here answers two of the
// three funnels Ryan named in one column.
//
// The third — Life / Disability — already HAS this stage under AC's own
// name for it, `quotes_delivered` ("Quotes Delivered"). DECISION: do NOT
// plant a second "Quotes Sent" column beside it. Two columns meaning one
// thing would split Ryan's live records between them, so the 3-day flag
// would catch whichever half he happened to use — the opposite of what he
// asked for. Instead PLANNING_QUOTES_SENT_STAGES below names both slugs
// as one concept, and the follow-up rule + his dashboard rail read that,
// so Life gets the flag today with no records moved. If Ryan wants the
// WORD to match across boards, renaming that column is one click in his
// own pipeline editor and needs no deploy.
//
// typical_days 3 matches the follow-up threshold: past it, the board's
// own stalled chip agrees with the dashboard rail rather than contradicting it.
const PLANNING_HEALTH_QUOTING: StageDef[] = [
  { slug: "quoting", label: "Quoting", typical_days: 5, sort_order: 1 },
  { slug: "quotes_sent", label: "Quotes Sent", typical_days: 3, sort_order: 2 },
  { slug: "conditionally_approved", label: "Need Forms or Conditionally Approved", typical_days: 7, sort_order: 3 },
  { slug: "awaiting_payment", label: "Need Payment", typical_days: 14, sort_order: 4 },
  { slug: "holding_short_term", label: "Holding (short-term)", typical_days: 30, sort_order: 5 },
  { slug: "waiting_long_term", label: "Waiting (long-term)", typical_days: 0, sort_order: 6 },
];

// The union of AC's two Life/DI boards (Need To Contact + Quoting) —
// one OS board, same treatment Mortgage Traditional gets. "Contact
// Made" comes from the Need To Contact board, where it was an empty
// column the deals export couldn't see; that board's "Life/DI -
// Holding" folds into the shared Holding column below (a board can't
// carry two Holdings).
const PLANNING_LIFE_DI: StageDef[] = [
  { slug: "life_di_need_to_contact", label: "Need To Contact", typical_days: 2, sort_order: 1 },
  { slug: "life_di_contact_made", label: "Contact Made", typical_days: 3, sort_order: 2 },
  { slug: "life_di_appointment_scheduled", label: "Appointment Scheduled", typical_days: 5, sort_order: 3 },
  { slug: "quoting", label: "Quoting", typical_days: 7, sort_order: 4 },
  { slug: "quotes_delivered", label: "Quotes Delivered", typical_days: 7, sort_order: 5 },
  { slug: "application_in_process", label: "Application In Process", typical_days: 21, sort_order: 6 },
  { slug: "holding", label: "Holding", typical_days: 0, sort_order: 7 },
];

/**
 * "The quotes are out and we're waiting on them" — every Planning
 * opportunity slug that means that moment, across all three of Ryan's
 * quoting funnels.
 *
 * TWO slugs, not one, and deliberately so (Ryan, 2026-08-25 — see the
 * long DECISION on PLANNING_HEALTH_QUOTING above). `quotes_sent` is the
 * new column on the Health Quoting board, which covers ACA, Short-Term
 * and Medicare. `quotes_delivered` is AC's own name for the identical
 * moment on the Life / Disability board, where the column already exists
 * and carries live records — reading it here is what gives Life the
 * 3-day follow-up flag without moving a single record or planting a
 * duplicate column.
 *
 * ONE source of truth on purpose: the follow-up Automation rule, Ryan's
 * dashboard attention rail, and the tests all read this list. A future
 * board rename adds a slug HERE and the three stay in step; a second
 * hardcoded copy is how the board and the dashboard start disagreeing.
 */
export const PLANNING_QUOTES_SENT_STAGES: readonly string[] = [
  "quotes_sent",
  "quotes_delivered",
];

const PLANNING_ANNUITY: StageDef[] = [
  { slug: "annuity_waiting_on_assets", label: "Waiting On Assets", typical_days: 14, sort_order: 1 },
  { slug: "annuity_contract_issued", label: "Contract Issued", typical_days: 7, sort_order: 2 },
  { slug: "annuity_policy_received", label: "Policy Received", typical_days: 7, sort_order: 3 },
  { slug: "annuity_policy_delivered", label: "Policy Delivered", typical_days: 0, sort_order: 4 },
];

// Wills & Trusts SALES phase (the AC pipeline split: these stages were
// Opportunity-side; In Process onward is the Processing template
// below). Stage set read from the migrated rows (need_to_contact /
// contact_made / appointment_scheduled).
const PLANNING_ESTATE_OPPORTUNITY: StageDef[] = [
  { slug: "need_to_contact", label: "Need To Contact", typical_days: 2, sort_order: 1 },
  { slug: "contact_made", label: "Contact Made", typical_days: 3, sort_order: 2 },
  { slug: "appointment_scheduled", label: "Appointment Scheduled", typical_days: 5, sort_order: 3 },
];

// Everything Planning sells that ends in a POLICY being issued and
// delivered — life, disability, health, and annuity.
//
// Ryan Delviken, screen recording 2026-08-08: he won two life insurance
// cases and both landed on the estate-plan drafting board below.
// "This transition progress would be, looks like a state planning
// pipeline, but really should be on the life insurance side."
//
// He was describing a real hole, not a mis-click. Planning had exactly
// ONE Processing board — the Wills & Trusts drafting board — so every
// won Planning opportunity, whatever the product, was filed as an estate
// plan and staged through Schedule Signing / Waiting to Sign. Those
// columns are meaningless for a term-life sale: the post-sale work there
// is an application going through underwriting until the carrier issues
// and the client receives the policy.
//
// This is the same class of gap Grant named on 2026-08-01 ("the win flow
// typed planning wins health_case blindly … and there is NO way to
// retype a service record afterward"). That fix gave the CASE a type it
// could change; nobody gave the PROCESSING record a board it could move
// to. Ryan walked into the half left open.
//
// DECISION: one delivery board for life/DI/health/annuity rather than
// three. They share one shape (submit → underwrite → pay → issue →
// deliver) and Planning is a two-advisor company; three near-identical
// boards would be three places to make the same edit. Annuity rides here
// too — its AC opportunity board already ends "Contract Issued → Policy
// Received → Policy Delivered", which is this shape. Estate plans keep
// their own board because their delivery genuinely differs (drafting and
// a signing ceremony, no carrier).
//
// DECISION: these stages were a STARTING SET, not a claim to know Ryan's
// workflow — see the RE-CUT below, which is him answering.
//
// ── THE RE-CUT (Ryan, 2026-08-25 — program session S10) ─────────────
//
// ryan-planning-views.md §6. Asked where Opportunities end and
// Processing begins, Ryan drew the line at the application — "Opportunities
// end at Application Submitted" — and named the five columns himself:
//
//   Application Submitted → App Under Review → App Approved
//     → Payment Received → Policy In-Force
//
// What changed and why, column by column:
//   * "Application In Process" → **Application Submitted**. Ryan's own
//     words, and the same phrase his Medicare care board already uses
//     (PLANNING_MEDICARE_CARE carries `application_submitted`). That
//     matters beyond tidiness: his dashboard's 10-day attention lane can
//     now read ONE slug across both boards instead of two, which is
//     exactly what stopped that lane covering Processing before.
//   * "Underwriting" → **App Under Review**, which absorbs it. Ryan's
//     framing: the carrier is looking at the application, and whether
//     that is formal underwriting or an eligibility check is the
//     carrier's business, not a column he wants to sort by.
//   * "Need Payment" splits into **App Approved** → **Payment Received**.
//     The old single column conflated "they said yes, now collect" with
//     "money's in" — two facts he chases differently.
//   * "Policy Received"/"Policy Delivered" → **Policy In-Force**. Two
//     columns for the handover collapse into the one fact that matters:
//     the coverage is live. This is ALSO the product-materialization
//     trigger stage now (shared/service-records/product-stage.ts) — a
//     policy in force IS the policy existing, which is the doctrine's own
//     test for when the product record gets created.
//
// Slugs are clean slugify(label) throughout. Every one of them is NEW, so
// records sitting on the old five slugs are orphaned until the re-homing
// data pass moves them — that pass is
// shared/migration/passes/planning-processing-rehome-pass.ts, previewed
// and run on Grant's word, never automatically. Board edits are
// configuration; moving live records is a data pass. Estate plans keep
// their own drafting board (PLANNING_CASE_DRAFT), untouched.
//
// Ryan owns it from here — Planning users can rename, reorder, add and
// remove these columns in the pipeline board editor without a deploy.
//
// SPLIT 2026-08-29 (Grant: "let Ryan do what he wants. Split life and
// health.") — this board used to carry every non-estate Planning
// product; Ryan pointed out it "looks like a life insurance case" while
// 29 of the 33 records that had ever been on it were Medicare/health,
// and exactly zero life cases were still open. Health/Medicare/dental/
// vision/STM/medishare KEEP this board and this array — they're the
// large majority and Ryan's own five-stage recut (2026-08-25) already
// fits them. Life/disability/annuity get their OWN board below
// (PLANNING_LIFE_CASE / planning_life_case), seeded from the SAME five
// stages: not a claim that life underwriting works identically to
// Medicare enrollment, only that there was no signal to invent
// different columns from, and Ryan can now diverge either board
// himself (the fix that made "Ryan owns it from here" literally true —
// the Board Setup link on a record page was gated to admins/
// company_admin, which excluded him; see the permission fix in this
// same change).
//
// PLANNING_LIFE_CASE below is a SEPARATE array literal with the same
// starting content, not a reused reference to this one — load-bearing,
// not stylistic. groupBoards() (below in this file) collapses declared
// pipeline keys into one board whenever two keys point at the exact
// same array OBJECT (it uses the array as a JS Map key, i.e. reference
// identity) — that collapsing is exactly how the genuine aliases
// elsewhere in this table work (mortgage:mortgage_loan_progression
// sharing MORTGAGE_TRADITIONAL with mortgage:*, two spellings of ONE
// board). Reusing this reference for planning_life_case would silently
// re-merge Ryan's two boards back into one editable row the instant this
// shipped — the opposite of what a split means. Two literals, even
// though identical today, is what keeps them two independently-editable
// boards; company-stage-templates.test.ts's board-count assertions are
// the guard that would catch a future accidental re-merge.
const PLANNING_LIFE_HEALTH_CASE: StageDef[] = [
  { slug: "application_submitted", label: "Application Submitted", typical_days: 10, sort_order: 1 },
  { slug: "app_under_review", label: "App Under Review", typical_days: 21, sort_order: 2 },
  { slug: "app_approved", label: "App Approved", typical_days: 7, sort_order: 3 },
  { slug: "payment_received", label: "Payment Received", typical_days: 7, sort_order: 4 },
  { slug: "policy_in_force", label: "Policy In-Force", typical_days: 0, sort_order: 5 },
];

// The Life board — split off 2026-08-29. See the comment above for the
// full why and for why this MUST be its own array literal rather than a
// reference to PLANNING_LIFE_HEALTH_CASE.
const PLANNING_LIFE_CASE: StageDef[] = [
  { slug: "application_submitted", label: "Application Submitted", typical_days: 10, sort_order: 1 },
  { slug: "app_under_review", label: "App Under Review", typical_days: 21, sort_order: 2 },
  { slug: "app_approved", label: "App Approved", typical_days: 7, sort_order: 3 },
  { slug: "payment_received", label: "Payment Received", typical_days: 7, sort_order: 4 },
  { slug: "policy_in_force", label: "Policy In-Force", typical_days: 0, sort_order: 5 },
];

// Group long-term care, sold to an EMPLOYER (employer groups, 2026-10-29;
// docs/design/employer-groups.md). The employer is the client for the sale:
// these two boards hold the employer's Opportunity and its implementation
// file, never an employee's coverage. Stages follow what Baker and
// BuddyIns described on 2026-10-27: BuddyIns runs the implementation
// (about 45 days after the employer commits) and enrollment, so Stewardship
// tracks the milestones and not the enrollment itself.
const PLANNING_GROUP_LTC_OPPORTUNITY: StageDef[] = [
  { slug: "discovery", label: "Discovery", typical_days: 7, sort_order: 1 },
  { slug: "quoting", label: "Quoting", typical_days: 14, sort_order: 2 },
  { slug: "proposal_presented", label: "Proposal Presented", typical_days: 14, sort_order: 3 },
  { slug: "decision", label: "Decision", typical_days: 14, sort_order: 4 },
];

const PLANNING_GROUP_LTC_IMPLEMENTATION: StageDef[] = [
  { slug: "agreement_signed", label: "Agreement Signed", typical_days: 7, sort_order: 1 },
  { slug: "pre_kickoff_with_buddyins", label: "Pre-Kickoff With BuddyIns", typical_days: 7, sort_order: 2 },
  { slug: "buddyins_implementing", label: "BuddyIns Implementing", typical_days: 45, sort_order: 3 },
  { slug: "enrollment_open", label: "Enrollment Open", typical_days: 21, sort_order: 4 },
  { slug: "enrollment_closed", label: "Enrollment Closed", typical_days: 7, sort_order: 5 },
  { slug: "carrier_issued", label: "Carrier Issued", typical_days: 0, sort_order: 6 },
];

/**
 * The Processing stage that means "the coverage is live" for Planning's
 * life / DI / health / annuity board.
 *
 * Exported so product-stage.ts's trigger table, the re-homing pass, and
 * the tests all name the same column — a typo in any one of them would
 * silently mean "no policy record until the record is marked complete",
 * which is the failure mode product-materialization exists to end.
 */
export const PLANNING_POLICY_IN_FORCE_STAGE = "policy_in_force";

/**
 * "The application is in, nobody has come back yet" — the Processing
 * stage Ryan's 10-day attention lane counts.
 *
 * Deliberately the SAME slug on two different boards: the new Processing
 * board above and the Medicare Ongoing Care board
 * (PLANNING_MEDICARE_CARE), which has carried `application_submitted`
 * since 2026-07-22. Renaming the Processing column to Ryan's own words
 * is what made them agree, so one lane can read both without a second
 * hardcoded slug drifting out of step.
 */
export const PLANNING_APPLICATION_SUBMITTED_STAGE = "application_submitted";

// Wills & Trusts drafting (AC's Processing-phase stages, verbatim).
// Payment precedes drafting on Ryan's board — the client pays, then
// Andrea drafts. The 07-11 pass had In Process first.
const PLANNING_CASE_DRAFT: StageDef[] = [
  { slug: "waiting_for_customer_payment", label: "Waiting for Customer Payment", typical_days: 7, sort_order: 1 },
  { slug: "in_process", label: "In Process", typical_days: 14, sort_order: 2 },
  { slug: "waiting_for_documents_to_arrive", label: "Waiting for Documents to Arrive", typical_days: 10, sort_order: 3 },
  { slug: "schedule_signing", label: "Schedule Signing", typical_days: 5, sort_order: 4 },
  { slug: "waiting_to_sign", label: "Waiting to Sign", typical_days: 7, sort_order: 5 },
  { slug: "post_signing_follow_up", label: "Post Signing Follow Up", typical_days: 0, sort_order: 6 },
];

// ─── Realty templates ─────────────────────────────────────────────
//
// REWORK (Grant, 2026-08-11): the pre-transaction "how far along is this
// buyer/seller" work — Shopping, Pre Approved, Listing Prep — moved OFF
// Processing and ONTO the Opportunity board; they're engagement stages,
// not delivery stages. Contact Made - Passive, Contact Made - Engaged,
// Active Engagement, and Passive Engagement are retired outright (Nate
// and Mark never worked the board through that granularity). Offer Made
// is retired from Processing too — it sat empty in AC and stays empty
// here. Records that were open on any of these five retired stages were
// hand-relocated by Nate/Grant before this change landed (Paul Kim +
// Tony Scott moved off Active/Passive Engagement; Emilie Brown's
// Processing record moved to Pre Approved, which is now an Opportunity
// stage — see the PR description for how that one's being handled).
// Closed (won/lost/fell-through) records on a retired stage are left
// exactly where they are; that stage is history the reports already
// counted, not live work.

const REALTY_OPPORTUNITY: StageDef[] = [
  { slug: "need_to_contact", label: "Need to Contact", typical_days: 2, sort_order: 1 },
  { slug: "contact_made", label: "Contact Made", typical_days: 3, sort_order: 2 },
  { slug: "appointment_scheduled", label: "Appointment Scheduled", typical_days: 5, sort_order: 3 },
  // Buy-side engagement, promoted from Processing (2026-08-11): a buyer is
  // "Shopping" homes, then "Pre Approved" and ready to write offers — both
  // are sales-pipeline states (open until they win a home), not delivery
  // work, so they belong here rather than on the Processing board.
  { slug: "shopping", label: "Shopping", typical_days: 30, sort_order: 4 },
  { slug: "pre_approved", label: "Pre Approved", typical_days: 30, sort_order: 5 },
  // Sell-side engagement, promoted from Processing (2026-08-11): getting a
  // property ready to list is still sales-pipeline work — the property
  // isn't live yet, so it isn't Processing's "Listed" column.
  { slug: "listing_prep", label: "Listing Prep", typical_days: 14, sort_order: 6 },
  { slug: "holding", label: "Holding", typical_days: 0, sort_order: 7 },
  { slug: "holding_less_than_3_months", label: "Holding Less Than 3 Months", typical_days: 90, sort_order: 8 },
  { slug: "holding_3_6_months", label: "Holding 3-6 Months", typical_days: 180, sort_order: 9 },
  { slug: "holding_6_12_months", label: "Holding 6-12 Months", typical_days: 0, sort_order: 10 },
  { slug: "holding_extended", label: "Holding Extended", typical_days: 0, sort_order: 11 },
  { slug: "stale_leads", label: "Stale Leads", typical_days: 0, sort_order: 12 },
];

// DECISION (Grant, 2026-07-31, reworked 2026-08-11): Realty still runs ONE
// combined Processing board — buy side and listing side share it — but as
// of 2026-08-11 the board starts once a property is genuinely live/under
// contract. Shopping / Pre Approved / Listing Prep / Offer Made all moved
// off (Shopping + Pre Approved + Listing Prep onto the Opportunity board
// above; Offer Made retired outright — it was always empty). See
// shared/realty/transaction-side.ts for how a won Opportunity now picks
// this board's entry stage per side (sell → Listed, buy → Opening Escrow,
// since a buyer is already under contract by the time they win).
const REALTY_TRANSACTION: StageDef[] = [
  // Prelisting (Nate, 2026-08-21): the two-week window between "the client
  // picked us" (won Opportunity) and the property going live. Nate's call:
  // this is delivery work — photos, listing agreement, lockbox, MLS draft —
  // so it lives on the Processing board, entered on win, ahead of Listed.
  // (The Opportunity board's "Listing Prep" stays what it is: pre-win
  // engagement before the client has committed.) Sell-side entry follows
  // automatically: realtyEntryStageSlug() picks the board's first column
  // for the sell side, which is now this one. The stages below renumber by one
  // (the parity test requires contiguous sort_order from 1).
  { slug: "prelisting", label: "Prelisting", typical_days: 14, sort_order: 1 },
  { slug: "listed", label: "Listed", typical_days: 30, sort_order: 2 },
  { slug: "opening_escrow", label: "Opening Escrow", typical_days: 5, sort_order: 3 },
  { slug: "due_diligence", label: "Due Diligence", typical_days: 10, sort_order: 4 },
  // BINSR = Buyer Inspection Notice and Seller Response. Grant, 2026-07-31:
  // "keep the BINSR Settlement Repair Negotiations", then renamed to the
  // shorter "Repair Negotiations" for the board (2026-08-11) — the slug
  // stays the AC-migrated one (slugify of the ORIGINAL long label) so the
  // one open record already sitting here isn't orphaned by the rename; see
  // the slug-parity exception list in tests/unit/company-stage-templates.
  // test.ts. AC runs it between Due Diligence and Appraisal In (inspection
  // findings are negotiated before the appraisal lands). typical_days 14:
  // repair negotiation is a back-and-forth with the other side, routinely
  // slower than the 10-day due-diligence window it follows.
  {
    slug: "binsr_settlement_repair_negotiations",
    label: "Repair Negotiations",
    typical_days: 14,
    sort_order: 5,
  },
  { slug: "appraisal_in", label: "Appraisal In", typical_days: 7, sort_order: 6 },
  { slug: "clear_to_close", label: "Clear To Close", typical_days: 3, sort_order: 7 },
  { slug: "closing_scheduled", label: "Closing Scheduled", typical_days: 5, sort_order: 8 },
  // AC ends the In Contract board with Closed - Compliance, then New
  // Build Waiting (the long-tail parking column).
  { slug: "closed_compliance", label: "Closed - Compliance", typical_days: 0, sort_order: 9 },
  // Label reads "New Building Waiting" per Grant (2026-08-11); slug stays
  // the AC-migrated `new_build_waiting` so the 6 records already parked
  // here aren't orphaned by the wording change — same slug-parity
  // exception pattern as the Repair Negotiations rename above.
  { slug: "new_build_waiting", label: "New Building Waiting", typical_days: 0, sort_order: 10 },
];

// Rentals (Grant, 2026-09-22: "we do help people find rentals from time to
// time. it is rare. but the OS needs to be able to handle this
// correctly"). A lease has no escrow, no appraisal, no repair
// negotiation and no clear-to-close, so a rental on REALTY_TRANSACTION
// would sit in a sale column and nag for a close date it will never have.
// Its own short board instead, one board for both sides the way the sale
// board is (shared/realty/rental.ts):
//
//   * Landlord side (we lease an owner's home out) enters at Prelisting,
//     the board's first column: the Residential Exclusive Right to
//     Sell/Rent is signed and the home is being readied.
//   * Tenant side (we find a client a home) enters at Tenant Applied:
//     the consultation is won when they apply for a home, the rental twin
//     of going under contract. realtyEntryStageSlug() picks it.
//
// The two application columns say "Tenant" on purpose. Stage slugs are
// shared vocabulary across companies (the tooltips in
// web/src/lib/stage-descriptions.ts are keyed by slug alone), and
// `application_submitted` already means a CARRIER application on
// Planning's boards.
//
// Closed - Compliance keeps the sale board's slug on purpose: the ADRE
// file review is the same review whether the contract was a sale or a
// lease. Nate and Mark can reshape this board in the Pipeline editor
// like any other; these are defaults, not doctrine.
const REALTY_RENTAL_TRANSACTION: StageDef[] = [
  { slug: "prelisting", label: "Prelisting", typical_days: 7, sort_order: 1 },
  { slug: "listed_for_rent", label: "Listed For Rent", typical_days: 21, sort_order: 2 },
  { slug: "tenant_applied", label: "Tenant Applied", typical_days: 5, sort_order: 3 },
  { slug: "tenant_approved", label: "Tenant Approved", typical_days: 5, sort_order: 4 },
  { slug: "lease_signed", label: "Lease Signed", typical_days: 7, sort_order: 5 },
  { slug: "move_in_scheduled", label: "Move In Scheduled", typical_days: 10, sort_order: 6 },
  { slug: "closed_compliance", label: "Closed - Compliance", typical_days: 0, sort_order: 7 },
];

// ─── Ongoing Care (renewal workflow) templates ────────────────────
//
// Grant's ask, 2026-07-22: care cycles get the same per-pipeline,
// editor-editable stage model the Opportunity/Processing boards have.
// A care pipeline is keyed by the linked service record's service_type
// (planning · ongoing_care · medicare_case); insurance keeps ONE
// pipeline across all policy types ('*') because Joy's renewal
// workflow is identical for home/auto/umbrella.
//
// Unlike opportunity/transaction, ongoing_care has NO global fallback:
// a care pipeline with no template here and no DB override is
// STAGE-LESS — the board shows the derived status tracker (Overdue /
// Due soon / …) instead of workflow columns. Stages are opt-in per
// pipeline, defined in the Pipeline boards editor.
//
// typical_days = 0 throughout: renewal cycles age against their
// anchor date (the care views' own overdue/due-soon logic), not the
// pipeline stalling heuristic.

// Insurance: the AC-parity renewal catalog (shared/renewals/stages.ts,
// locked by Grant 2026-07-11) re-expressed as StageDefs — one source
// list, two shapes.
//
// "Complete" is the board's done stage (Ongoing Care v2, 2026-10-02): the
// one place a default marks a stage done, because Joy's board already has
// an explicit terminal and Grant's 2026-07-11 lock named it as the end of
// the cycle. A saved override in Pipeline boards replaces this whole list,
// so the owner's choice always wins. Planning boards get NO done default:
// Ryan adds "Renewal Complete" and ticks it himself.
const INSURANCE_CARE_RENEWAL: StageDef[] = RENEWAL_STAGES.map((s, i) => ({
  slug: s.key,
  label: s.label,
  typical_days: 0,
  sort_order: i + 1,
  ...(s.key === "complete" ? { counts_as_done: true } : {}),
}));

// Planning · Medicare: Grant's stage list (2026-07-22), mapped from
// the AC "PLANNING: Health - Medicare Renewal" board. AC's Hopper /
// PDP / Advantage columns were automation holding-pens, not workflow
// stages — their sends are Automations rules (when.time.renewal +
// service type / carrier conditions). DECISION: no "Complete" terminal
// stage, per Grant's list — cycle completion is the roll-forward on
// the anchor date, not a column. Editable in the Pipeline boards
// editor without a deploy.
const PLANNING_MEDICARE_CARE: StageDef[] = [
  { slug: "all_active_members", label: "All Active Members", typical_days: 0, sort_order: 1 },
  { slug: "info_received", label: "Info Received", typical_days: 0, sort_order: 2 },
  { slug: "quoting", label: "Quoting", typical_days: 0, sort_order: 3 },
  { slug: "stay_recommended", label: "Stay Recommended", typical_days: 0, sort_order: 4 },
  { slug: "change_recommended", label: "Change Recommended", typical_days: 0, sort_order: 5 },
  { slug: "appointment_scheduled", label: "Appointment Scheduled", typical_days: 0, sort_order: 6 },
  { slug: "application_submitted", label: "Application Submitted", typical_days: 0, sort_order: 7 },
  { slug: "waiting", label: "Waiting", typical_days: 0, sort_order: 8 },
];

// Planning · ACA (health_case): AC's "Health - ACA Renewal" board,
// added at cutover (Grant, 2026-07-31) — Medicare had a care board
// since 07-22 but ACA never got one, so Andrea's 187 active ACA
// cycles had no columns at all.
//
// REKEYED 2026-09-23 (Grant, after Andrea flagged 14 questionnaire
// returns sitting under "Stay Recommended" with no recommendation
// made). Andrea's 2026-09-14 rename in the Pipeline boards editor
// changed column NAMES but — by design, so a rename never orphans a
// card sitting in that column — kept each row's original slug. That
// meant the labels below shifted relative to their own keys: what now
// reads "Stay Recommended" was still the ORIGINAL "Info Received -
// Need to Quote" key underneath, so the ACA questionnaire automation
// (which moves a card there on submission) kept landing families
// under the wrong name. Labels and order here are Andrea's, verbatim.
// Only the slugs changed, to the ones her labels actually say — the
// same fix `planning-aca-care-board-key-fix-pass.ts` applies to the
// live `stage_templates` override, and to the cycles that were sitting
// on the old keys. See that pass for the full mapping and the data
// remap.
//   * "Hopper - All Active ACA Deals" → "All Active Members" predates
//     this rekey: "Deal" is banned vocabulary (CLAUDE.md vocabulary
//     lock), and it matches the Medicare board's entry column.
const PLANNING_ACA_CARE: StageDef[] = [
  { slug: "all_active_members", label: "All Active Members", typical_days: 0, sort_order: 1 },
  { slug: "initial_email_sent", label: "Initial Email Sent", typical_days: 0, sort_order: 2 },
  { slug: "info_received", label: "Info Received", typical_days: 0, sort_order: 3 },
  { slug: "quoting", label: "Quoting", typical_days: 0, sort_order: 4 },
  { slug: "stay_recommended", label: "Stay Recommended", typical_days: 0, sort_order: 5 },
  { slug: "change_recommended", label: "Change Recommended", typical_days: 0, sort_order: 6 },
  { slug: "appointment_scheduled", label: "Appointment Scheduled", typical_days: 0, sort_order: 7 },
  { slug: "application_submitted", label: "Application Submitted", typical_days: 0, sort_order: 8 },
  { slug: "need_payment", label: "Need Payment", typical_days: 0, sort_order: 9 },
  { slug: "waiting", label: "Waiting", typical_days: 0, sort_order: 10 },
];

// Financial · Annual Review & Tax Planning: AC's "FINANCIAL: Review"
// board, brought over at Grant's direction (2026-08-19). ONE pipeline
// covering the whole calendar year — tax season and annual review
// season are two halves of a single 12-month cycle, exactly as Jake
// and Brian run it today, not two separate programs.
//
// The cycle, anchored January 1: the tax letter goes out in January,
// two follow-up emails land in March, August 1 flips the household
// into annual review season, the request email goes out August 28,
// the form comes back, the meeting gets booked. Then the anchor comes
// round again.
//
// Copied from AC column-for-column with two deliberate drops (Grant,
// 2026-08-19) and the automation instructions stripped out of the
// names (same call he made for the ACA board — "people had no idea
// what was going to automate in each stage; don't need that in the
// OS"). The instructions live in the Automations rules that do the
// work, where they can be read and changed.
//
// Dropped from AC:
//   * "Hopper" — AC's between-cycles holding pen, where a household
//     parked until its next date arrived. The OS has no use for it:
//     a care cycle that reaches its end rolls forward on the anchor
//     date, which is the same behaviour without a column someone has
//     to remember to drag cards back into. Grant: "hopper is where
//     people would sit until the dates came."
//   * "Under 100k: HOLD ... for Fee Update Email" — last year only
//     (Grant, 2026-08-19). A one-season hold has no business being a
//     permanent column.
//
// No terminal "Complete" stage, matching the Medicare and ACA boards:
// completion IS the roll-forward.
const FINANCIAL_REVIEW_CARE: StageDef[] = [
  { slug: "send_tax_letter", label: "Send Tax Letter", typical_days: 0, sort_order: 1 },
  { slug: "tax_letter_sent", label: "Tax Letter Sent", typical_days: 0, sort_order: 2 },
  { slug: "taxes_received", label: "Taxes Received", typical_days: 0, sort_order: 3 },
  { slug: "tax_explainer_complete", label: "Tax Explainer Complete", typical_days: 0, sort_order: 4 },
  { slug: "annual_meeting_request", label: "Annual Meeting Request", typical_days: 0, sort_order: 5 },
  { slug: "form_received", label: "Form Received", typical_days: 0, sort_order: 6 },
  { slug: "meeting_scheduled", label: "Meeting Scheduled", typical_days: 0, sort_order: 7 },
];

// ─── Generic fallbacks (the original DEFAULT templates) ───────────

const DEFAULT_OPPORTUNITY_STAGES: StageDef[] = [
  { slug: "lead", label: "Lead", typical_days: 1, sort_order: 1 },
  { slug: "contact_made", label: "Contact made", typical_days: 2, sort_order: 2 },
  { slug: "info_gathered", label: "Info gathered", typical_days: 2, sort_order: 3 },
  { slug: "quote_sent", label: "Quote sent", typical_days: 5, sort_order: 4 },
  { slug: "decision", label: "Decision", typical_days: 3, sort_order: 5 },
];

const DEFAULT_TRANSACTION_STAGES: StageDef[] = [
  { slug: "application", label: "Application", typical_days: 1, sort_order: 1 },
  { slug: "underwriting", label: "Underwriting", typical_days: 5, sort_order: 2 },
  { slug: "approved", label: "Approved", typical_days: 1, sort_order: 3 },
  { slug: "active", label: "Active", typical_days: 0, sort_order: 4 },
];

// ─── Lookup table ─────────────────────────────────────────────────
//
// Composite key: `${companySlug}:${workflow}:${type}`. Use '*' for
// "any type" within a company-workflow pair. The named type keys are
// the REAL opportunity_type / transaction_type values on records
// (verified against production, 2026-07-11) plus the values the New
// Opportunity modal / Mark Won flow assigns to OS-created records.
//
// NOT keyed (documented in the parity PR): the 39 Klinkloans investor-
// prospect rows share opportunity_type `private_money_loan` with
// borrower deals, so their board needs a record re-type first.

const TEMPLATES: Record<string, StageDef[]> = {
  // Insurance
  "insurance:opportunity:*": INSURANCE_QUOTE_STAGES,
  "insurance:opportunity:insurance_quote": INSURANCE_QUOTE_STAGES,
  "insurance:opportunity:home_policy_quote": INSURANCE_QUOTE_STAGES,
  "insurance:opportunity:auto_policy_quote": INSURANCE_QUOTE_STAGES,
  "insurance:opportunity:umbrella_policy_quote": INSURANCE_QUOTE_STAGES,
  "insurance:opportunity:landlord_policy_quote": INSURANCE_QUOTE_STAGES,
  "insurance:opportunity:secondary_home_policy_quote": INSURANCE_QUOTE_STAGES,
  "insurance:opportunity:motorcycle_policy_quote": INSURANCE_QUOTE_STAGES,
  "insurance:opportunity:rv_policy_quote": INSURANCE_QUOTE_STAGES,
  "insurance:transaction:*": INSURANCE_POLICY_ISSUANCE,
  "insurance:transaction:policy_issuance": INSURANCE_POLICY_ISSUANCE,

  // Mortgage — Traditional + Private Money
  "mortgage:opportunity:*": MORTGAGE_TRAD_OPPORTUNITY,
  "mortgage:opportunity:mortgage_lead": MORTGAGE_TRAD_OPPORTUNITY,
  "mortgage:opportunity:mortgage_prequalification": MORTGAGE_TRAD_OPPORTUNITY,
  "mortgage:opportunity:pre_qualification": MORTGAGE_TRAD_OPPORTUNITY,
  "mortgage:opportunity:purchase_mortgage": MORTGAGE_TRAD_OPPORTUNITY,
  "mortgage:opportunity:refinance_mortgage": MORTGAGE_TRAD_OPPORTUNITY,
  "mortgage:opportunity:private_money_loan": MORTGAGE_PM_OPPORTUNITY,
  "mortgage:transaction:*": MORTGAGE_TRAD_PROCESSING,
  "mortgage:transaction:mortgage_loan_progression": MORTGAGE_TRAD_PROCESSING,
  // Migrated PM transactions carry `private_money_loan`; Mark-Won-
  // created ones carry `private_money_loan_progression`. Same board.
  "mortgage:transaction:private_money_loan": MORTGAGE_PM_PROCESSING,
  "mortgage:transaction:private_money_loan_progression": MORTGAGE_PM_PROCESSING,

  // Financial
  "financial:opportunity:*": FINANCIAL_OPPORTUNITY,
  "financial:opportunity:financial_needs_assessment": FINANCIAL_OPPORTUNITY,
  "financial:opportunity:financial_lead": FINANCIAL_OPPORTUNITY,
  "financial:opportunity:financial_membership": FINANCIAL_MEMBERSHIP,
  "financial:transaction:*": FINANCIAL_ONBOARDING,
  "financial:transaction:financial_client_onboarding": FINANCIAL_ONBOARDING,

  // Planning — health contact / health quoting / life-DI / annuity /
  // estate. '*' falls back to the contact funnel (the most generic).
  "planning:opportunity:*": PLANNING_HEALTH_CONTACT,
  "planning:opportunity:health_lead": PLANNING_HEALTH_CONTACT,
  "planning:opportunity:insurance_quote": PLANNING_HEALTH_QUOTING,
  "planning:opportunity:life_di_lead": PLANNING_LIFE_DI,
  // Long-term care rides the Life / Disability funnel (same board: the
  // canonical key stays `life_di_lead`, which sorts first, so existing
  // board edits keep applying).
  "planning:opportunity:long_term_care": PLANNING_LIFE_DI,
  "planning:opportunity:annuity": PLANNING_ANNUITY,
  // Employer groups: the employer's sale and its implementation file.
  "planning:opportunity:group_ltc": PLANNING_GROUP_LTC_OPPORTUNITY,
  "planning:transaction:group_ltc_implementation": PLANNING_GROUP_LTC_IMPLEMENTATION,
  "planning:opportunity:estate_plan": PLANNING_ESTATE_OPPORTUNITY,
  "planning:opportunity:estate_plan_consultation": PLANNING_ESTATE_OPPORTUNITY,
  // The '*' fallback stays on the drafting board deliberately: it is what
  // an UNDECLARED planning transaction_type already resolves to today, and
  // repointing it would silently re-column any such record. New wins are
  // routed explicitly by the opportunity→product map, so nothing relies on
  // the fallback to reach the right board.
  "planning:transaction:*": PLANNING_CASE_DRAFT,
  "planning:transaction:estate_plan": PLANNING_CASE_DRAFT,
  "planning:transaction:estate_plan_case": PLANNING_CASE_DRAFT,
  "planning:transaction:life_health_case": PLANNING_LIFE_HEALTH_CASE,
  "planning:transaction:planning_life_case": PLANNING_LIFE_CASE,

  // Realty
  "realty:opportunity:*": REALTY_OPPORTUNITY,
  "realty:opportunity:realty_lead": REALTY_OPPORTUNITY,
  "realty:opportunity:realty_purchase": REALTY_OPPORTUNITY,
  "realty:opportunity:realty_shopping": REALTY_OPPORTUNITY,
  "realty:opportunity:listing_consultation": REALTY_OPPORTUNITY,
  "realty:opportunity:buyer_consultation": REALTY_OPPORTUNITY,
  // Rentals share the engagement funnel: a tenant still shops, a
  // landlord's home is still prepared, and the Holding stages fit both.
  "realty:opportunity:tenant_consultation": REALTY_OPPORTUNITY,
  "realty:opportunity:landlord_consultation": REALTY_OPPORTUNITY,
  "realty:transaction:*": REALTY_TRANSACTION,
  "realty:transaction:realty_transaction": REALTY_TRANSACTION,
  "realty:transaction:realty_purchase": REALTY_TRANSACTION,
  "realty:transaction:realty_rental": REALTY_RENTAL_TRANSACTION,
  // DELIBERATELY NOT DECLARED: `realty_listing` (4 production records, 3
  // open on 2026-08-07). It shows in the Board picker as its own stray
  // option, which is untidy — but declaring it HERE, against
  // REALTY_TRANSACTION, is worse than untidy. The processing-board repair
  // pass treats an undeclared type as a registry gap it must not touch,
  // and a declared one as a board whose columns it may correct a record
  // onto. Those three live listings sit at `listed_for_listings`, which
  // this board doesn't have, so declaring the type would let the pass
  // "repair" them onto the board's entry column — resetting a live
  // listing's clock regardless of which stage that entry column names.
  // That is the exact damage the 2026-07-31 production dry run caught
  // (see the long comment in
  // shared/migration/passes/processing-board-repair-pass.ts and its
  // regression test). Still true after the 2026-08-11 rework, which
  // changed the entry column's slug (now `listed`) but not this decision.
  //
  // The honest fix is the one that pass already names: Nate / Mark declare
  // the listing pipeline WITH ITS OWN STAGES. That is an owner decision
  // about sell-side workflow, not a side effect of collapsing duplicates.

  // Ongoing Care (renewal workflow) pipelines. Deliberately NO
  // '*:ongoing_care:*' fallback — undeclared care pipelines are
  // stage-less (see the Ongoing Care section comment above).
  "insurance:ongoing_care:*": INSURANCE_CARE_RENEWAL,
  "planning:ongoing_care:medicare_case": PLANNING_MEDICARE_CARE,
  "planning:ongoing_care:health_case": PLANNING_ACA_CARE,
  // Company-wide like insurance: the annual review + tax cycle is the
  // same for a Managed Portfolio, a Financial Plan, or a Retirement
  // Plan, so it does not split by service type.
  "financial:ongoing_care:*": FINANCIAL_REVIEW_CARE,

  // Global fallbacks
  "*:opportunity:*": DEFAULT_OPPORTUNITY_STAGES,
  "*:transaction:*": DEFAULT_TRANSACTION_STAGES,
};

// ─── Pipeline enumeration ─────────────────────────────────────────
//
// A "pipeline" (the user-facing "board") is a concrete (company, type)
// combination — e.g. Mortgage · Private Money Loan. The Processing /
// Opportunity boards let the user focus on ONE pipeline so its full
// stage set fits on screen rather than unioning every company+product
// pipeline at once. This enumerates the named pipelines the templates
// define (the '*' company-default + global-fallback keys are skipped —
// they're resolution fallbacks, not selectable pipelines).

export interface PipelineRef {
  /** Company slug — 'mortgage', 'insurance', … */
  companySlug: string;
  /** opportunity_type / transaction_type slug. */
  type: string;
  /** Humanized display label for the type. */
  label: string;
}

/** Title-case a slug for display ("private_money_loan_progression" →
 *  "Private Money Loan Progression"). */
function humanizeSlug(slug: string): string {
  return slug
    .split(/[_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * The named pipelines (boards) defined for a workflow, derived from the
 * template keys. Deterministic order: grouped by company slug, then by
 * type slug — so a selector reads stably. Excludes the '*' wildcard keys
 * (company-wide + global fallbacks) since those aren't pickable pipelines.
 */
export function listPipelines(workflow: Workflow): PipelineRef[] {
  const seen = new Set<string>();
  const refs: PipelineRef[] = [];
  for (const key of Object.keys(TEMPLATES)) {
    const [companySlug, wf, type] = key.split(":");
    if (!companySlug || !type || wf !== workflow) continue;
    if (companySlug === "*" || type === "*") continue;
    const id = `${companySlug}:${type}`;
    if (seen.has(id)) continue;
    seen.add(id);
    refs.push({ companySlug, type, label: humanizeSlug(type) });
  }
  refs.sort((a, b) =>
    a.companySlug !== b.companySlug
      ? a.companySlug.localeCompare(b.companySlug)
      : a.type.localeCompare(b.type),
  );
  return refs;
}

// ─── Boards: the collapsed, user-facing view of the pipelines ──────
//
// Grant, 2026-08-07: "Clean it up everywhere." The TEMPLATES table above
// declares one key per opportunity_type / transaction_type, but MOST of
// those keys point at the SAME stage list — five Realty opportunity types
// share REALTY_OPPORTUNITY, four Insurance types share
// INSURANCE_QUOTE_STAGES, and so on. Enumerated key-by-key that reads as
// 24 Opportunity + 9 Processing pipelines; there are only 11 + 5 actually
// different ones.
//
// Two costs of the un-collapsed view, both real:
//   * The editor asked the domain owner to make the same edit once per
//     alias. Miss one and cards of that type sit in a column its siblings
//     don't have — a silent divergence with no upside, since the aliases
//     are declared identical ON PURPOSE.
//   * The Processing board's Board picker offered each alias separately,
//     so picking one HID the rest: "Estate Plan" hid the 9 open
//     `estate_plan_case` files; "Realty Transaction" hid `realty_purchase`
//     and `realty_listing`; Private Money's two spellings split Mike's book.
//
// A BOARD is therefore a group of (company, type) keys that share one
// stage list. It has a canonical type — the company-wide '*' when the
// group contains it, else the alphabetically-first concrete type — which
// is where an edit is STORED, and `coveredTypes`, every concrete type the
// board speaks for.
//
// DECISION: grouping is by stage-array IDENTITY (the same constant object
// referenced from several keys), not by comparing stage CONTENT. Identity
// is exactly the statement "the config author declared these one board";
// content equality would merge two boards that happen to coincide today,
// which is a surprise nobody asked for. A test asserts no two distinct
// arrays within a company are content-identical, so a future copy-paste
// that SHOULD have shared a constant is caught rather than silently
// splitting into two boards.
//
// listPipelines() above is unchanged and still enumerates every declared
// key — the won-conversion parity guards walk it to prove every
// opportunity type maps to a product for its own company (the drift bug
// Joey Ellis hit on 2026-07-30). Collapsing what is PICKABLE must never
// shrink what is CHECKED, so the two functions stay separate.

export interface BoardRef extends PipelineRef {
  /**
   * Every concrete (non-wildcard) type this board covers. May be empty
   * when a company declares only a '*' default. The board's own `type`
   * (the canonical key) is included whenever it is itself concrete.
   */
  coveredTypes: string[];
}

// Display names for boards whose canonical key is the company-wide '*'
// (which humanizes to nothing useful) or whose alphabetically-first type
// isn't the clearest name for the group. Anything not listed here falls
// back to the humanized canonical type, exactly as listPipelines does.
const BOARD_LABELS: Record<string, string> = {
  // Opportunities
  "insurance:opportunity:*": "Insurance Quote",
  // Pairs with "Private Money Loan" — the two Mortgage origination boards.
  "mortgage:opportunity:*": "Traditional Mortgage",
  "financial:opportunity:*": "Financial Needs Assessment",
  // Covers `health_lead`, whose New Opportunity label is "Health Coverage".
  "planning:opportunity:*": "Health Coverage",
  // Ryan's health QUOTING funnel. Its type slug is `insurance_quote` — the
  // documented collision with the Insurance company's P&C quote type — so
  // the humanized default would put two boards named "Insurance Quote" in
  // one picker, under different companies. Name it what it is.
  "planning:opportunity:insurance_quote": "Health Quoting",
  // Humanizing the slug gives "Life Di Lead"; this is the New Opportunity
  // modal's own label for the same funnel.
  "planning:opportunity:life_di_lead": "Life / Disability",
  "realty:opportunity:*": "Realty Lead",
  // Processing
  "insurance:transaction:*": "Policy Issuance",
  // Grant, 2026-08-10: paired with "Private Money Loan" below the way the
  // Opportunities picker already names these same two Mortgage books
  // ("Traditional Mortgage" / "Private Money Loan") — not the internal
  // workflow-tag name ("Mortgage Loan Progression").
  "mortgage:transaction:*": "Traditional Mortgage",
  // Canonical is `private_money_loan` (alphabetically first of the two
  // spellings). Matches the Opportunities picker's name for the same book.
  "mortgage:transaction:private_money_loan": "Private Money Loan",
  "financial:transaction:*": "Financial Client Onboarding",
  "planning:transaction:*": "Estate Plan Case",
  "planning:opportunity:group_ltc": "Group Long-Term Care",
  "planning:transaction:group_ltc_implementation": "Group Long-Term Care Implementation",
  // Named for the products on it, not for the phase — Ryan looking for a
  // won sale should recognize the board without being taught a new word.
  // Humanizing the slug would give "Life Health Case".
  //
  // RENAMED 2026-08-29 (was "Life & Health Case") when life split onto
  // its own board below — the combined name stopped describing what was
  // actually on it (Ryan: it "looks like a life insurance case" when 29
  // of 33 records were ever Medicare/health and zero life cases were
  // open). The transaction_type slug itself is UNCHANGED — only the
  // display label — so this stays the same board every existing life_
  // health_case record and every stored reference already points at;
  // renaming the type slug would be a bigger, unforced move for a label
  // change alone.
  "planning:transaction:life_health_case": "Health & Medicare Case",
  // The new board: life, disability, and annuity — the medically-
  // underwritten products that used to share the board above.
  "planning:transaction:planning_life_case": "Life Case",
  "realty:transaction:*": "Realty Transaction",
  "realty:transaction:realty_rental": "Realty Rental",
};

interface BoardGroup {
  types: string[];
  hasWildcard: boolean;
}

/** company → (shared stage array → the keys that reference it). */
function groupBoards(workflow: Workflow): Map<string, Map<StageDef[], BoardGroup>> {
  const byCompany = new Map<string, Map<StageDef[], BoardGroup>>();
  for (const key of Object.keys(TEMPLATES)) {
    const [companySlug, wf, type] = key.split(":");
    if (!companySlug || !type || wf !== workflow) continue;
    // The global ('*' company) fallback is a resolution backstop, never a
    // board any company owns.
    if (companySlug === "*") continue;
    const stages = TEMPLATES[key];
    if (!stages) continue;
    let groups = byCompany.get(companySlug);
    if (!groups) {
      groups = new Map();
      byCompany.set(companySlug, groups);
    }
    let group = groups.get(stages);
    if (!group) {
      group = { types: [], hasWildcard: false };
      groups.set(stages, group);
    }
    if (type === "*") group.hasWildcard = true;
    else group.types.push(type);
  }
  return byCompany;
}

function canonicalTypeFor(group: BoardGroup): string {
  if (group.hasWildcard) return "*";
  // Deterministic: the same board always stores its override under the
  // same key, whatever order the TEMPLATES literal happens to be in.
  return [...group.types].sort()[0] ?? "*";
}

/**
 * The selectable/editable BOARDS for a workflow — one per distinct stage
 * list per company, rather than one per declared type. This is what the
 * Pipeline boards editor lists and what the Processing board's Board
 * picker offers.
 *
 * Deterministic order: by company slug, then canonical type.
 */
export function listBoards(workflow: Workflow): BoardRef[] {
  const refs: BoardRef[] = [];
  for (const [companySlug, groups] of groupBoards(workflow)) {
    for (const group of groups.values()) {
      const type = canonicalTypeFor(group);
      const coveredTypes = [...group.types].sort();
      refs.push({
        companySlug,
        type,
        label:
          BOARD_LABELS[`${companySlug}:${workflow}:${type}`] ??
          humanizeSlug(type),
        coveredTypes,
      });
    }
  }
  refs.sort((a, b) =>
    a.companySlug !== b.companySlug
      ? a.companySlug.localeCompare(b.companySlug)
      : a.type.localeCompare(b.type),
  );
  return refs;
}

/**
 * The canonical board type for a record's (company, type) — i.e. where an
 * edit to the board this record sits on is stored.
 *
 * Returns `type` unchanged when the type isn't declared in TEMPLATES: a
 * custom or migrated pipeline keeps its own identity rather than being
 * folded into a board it was never declared part of.
 */
export function resolveBoardType(
  companySlug: string,
  workflow: Workflow,
  type: string,
): string {
  for (const group of groupBoards(workflow).get(companySlug)?.values() ?? []) {
    if (type === "*" ? group.hasWildcard : group.types.includes(type)) {
      return canonicalTypeFor(group);
    }
  }
  return type;
}

/**
 * Every concrete type a board speaks for, given its canonical type. Used
 * to filter a list by "this board" rather than by one of its aliases.
 *
 * An unknown/custom board answers `[canonicalType]` — it speaks only for
 * itself. The one case that answers EMPTY is a board whose canonical type
 * is the company-wide '*' and which declares no concrete types: there is
 * no type value that would match its records, so a caller must scope by
 * company instead. Returning `["*"]` there would build a filter matching
 * nothing and quietly show an empty board — the failure this whole change
 * exists to remove.
 */
export function boardCoveredTypes(
  companySlug: string,
  workflow: Workflow,
  canonicalType: string,
): string[] {
  const board = listBoards(workflow).find(
    (b) => b.companySlug === companySlug && b.type === canonicalType,
  );
  if (!board) return canonicalType === "*" ? [] : [canonicalType];
  if (board.coveredTypes.length > 0) return board.coveredTypes;
  return canonicalType === "*" ? [] : [canonicalType];
}

// The concrete type to STAMP on a record moved onto a board whose
// canonical key is the company-wide '*'. Every such board also declares a
// principal concrete type (see TEMPLATES) — this names it explicitly
// rather than deriving it from the display label, so renaming a board
// can never change what gets written to a record.
const BOARD_TARGET_TYPES: Record<string, string> = {
  "insurance:transaction:*": "policy_issuance",
  "mortgage:transaction:*": "mortgage_loan_progression",
  "financial:transaction:*": "financial_client_onboarding",
  // Not `estate_plan`: the open estate files in production carry
  // `estate_plan_case`, so a record moved onto this board should join them
  // rather than start a second spelling.
  "planning:transaction:*": "estate_plan_case",
  "realty:transaction:*": "realty_transaction",
};

/**
 * The concrete type a record must carry to sit on a given board — i.e.
 * what a "move this record to that board" action writes.
 *
 * A board with a concrete canonical key answers itself. A board canonical
 * on '*' answers its declared principal type. Returns null when a board
 * has no concrete type at all (nothing could be written to put a record
 * on it), so callers refuse rather than invent a slug.
 */
export function boardTargetType(
  companySlug: string,
  workflow: Workflow,
  canonicalType: string,
): string | null {
  if (canonicalType !== "*") return canonicalType;
  const explicit = BOARD_TARGET_TYPES[`${companySlug}:${workflow}:*`];
  if (explicit) return explicit;
  const covered = boardCoveredTypes(companySlug, workflow, "*");
  return covered.length > 0 ? (covered[0] as string) : null;
}

/**
 * Boards that belong to an EMPLOYER's sale, not to a household's. A family's
 * record must never be moved onto one, nor an employer's file off it, so
 * they are never offered as a move target (employer groups, 2026-10-29).
 */
const EMPLOYER_ONLY_BOARD_TYPES: ReadonlySet<string> = new Set([
  "group_ltc",
  "group_ltc_implementation",
]);

/**
 * The boards of one company a record may be MOVED between, each paired
 * with the concrete type that move writes. Boards with no concrete type
 * are omitted — they aren't reachable targets. Employer-only boards are
 * omitted too (EMPLOYER_ONLY_BOARD_TYPES).
 */
export function movableBoards(
  companySlug: string,
  workflow: Workflow,
): { targetType: string; label: string; coveredTypes: string[] }[] {
  const out: { targetType: string; label: string; coveredTypes: string[] }[] = [];
  for (const board of listBoards(workflow)) {
    if (board.companySlug !== companySlug) continue;
    if (EMPLOYER_ONLY_BOARD_TYPES.has(board.type)) continue;
    const targetType = boardTargetType(companySlug, workflow, board.type);
    if (!targetType) continue;
    out.push({
      targetType,
      label: board.label,
      coveredTypes: boardCoveredTypes(companySlug, workflow, board.type),
    });
  }
  return out;
}

export function lookupStageTemplate(key: StageTemplateKey): StageTemplate {
  const tryKeys = [
    `${key.companySlug}:${key.workflow}:${key.type}`,
    `${key.companySlug}:${key.workflow}:*`,
    `*:${key.workflow}:*`,
  ];
  for (const k of tryKeys) {
    const stages = TEMPLATES[k];
    if (stages) {
      return { stages, total: stages.length };
    }
  }
  // ongoing_care has no global fallback by design: an undeclared care
  // pipeline is stage-less (the board shows the status tracker).
  if (key.workflow === "ongoing_care") {
    return { stages: [], total: 0 };
  }
  // Should never hit — the global fallbacks are always present.
  const fallback =
    key.workflow === "opportunity"
      ? DEFAULT_OPPORTUNITY_STAGES
      : DEFAULT_TRANSACTION_STAGES;
  return { stages: fallback, total: fallback.length };
}

/**
 * The Ongoing Care pipelines shown in the Pipeline boards editor + the
 * staged renewal board. Unlike listPipelines(), this INCLUDES the
 * insurance '*' entry — for care, "all policy types" is itself the one
 * insurance pipeline (Joy's renewal workflow doesn't split by policy
 * type), so it must be pickable/editable rather than treated as a
 * hidden resolution fallback.
 */
export function listCarePipelineRefs(): PipelineRef[] {
  return [
    { companySlug: "insurance", type: "*", label: "Renewal Plan (all policy types)" },
    // Financial is company-wide for the same reason insurance is — one
    // review cycle across every Financial service type.
    {
      companySlug: "financial",
      type: "*",
      label: "Annual Review & Tax Planning (all Financial services)",
    },
    ...listPipelines("ongoing_care"),
  ];
}

// ─── Cross-template stage ranking ─────────────────────────────────
//
// The all-companies Opportunity Board mixes stages from every company,
// so there is no single template to order by. We derive a global rank
// from the canonical templates: a stage slug ranks by the EARLIEST
// sort_order it carries in any template that defines it (e.g.
// `contact_made` is sort_order 2 wherever it appears, so it sorts left
// of `quotes_delivered`). Slugs no template defines (custom / migrated
// stages) rank after the known ones and fall back to alphabetical at
// the call site. This keeps a single-company board reading left→right
// while degrading gracefully for the mixed board.

let GLOBAL_STAGE_RANK: Map<string, number> | null = null;

function buildGlobalStageRank(): Map<string, number> {
  const rank = new Map<string, number>();
  for (const stages of Object.values(TEMPLATES)) {
    for (const def of stages) {
      const existing = rank.get(def.slug);
      if (existing === undefined || def.sort_order < existing) {
        rank.set(def.slug, def.sort_order);
      }
    }
  }
  return rank;
}

/**
 * Left-to-right rank for an opportunity/transaction stage slug across
 * all companies. Lower sorts first. Unknown slugs return
 * `UNKNOWN_STAGE_RANK` so callers can tiebreak them alphabetically.
 */
export const UNKNOWN_STAGE_RANK = 9999;

export function globalStageRank(slug: string): number {
  if (!GLOBAL_STAGE_RANK) GLOBAL_STAGE_RANK = buildGlobalStageRank();
  return GLOBAL_STAGE_RANK.get(slug) ?? UNKNOWN_STAGE_RANK;
}

// ─── Care entry-stage slugs ───────────────────────────────────────────
//
// The FIRST stage of every configured ongoing_care pipeline. A care
// cycle whose renewal_stage is NULL lives in its pipeline's entry column
// (the board's grouping rule, and the NULL semantics stages.ts declares),
// so any server-side filter or count matching an entry-stage slug must
// also match NULL — this set is how the list's stage filter knows which
// slugs those are (listRhythmsForView, 2026-08-20).
//
// Sourced from the static config catalogs, deliberately NOT from the
// stage_templates DB overlay: consulting the overlay would make a pure
// filter async for an admin rarity (reordering a board's first column in
// the Pipeline-boards editor). If that rarity ever happens, this set and
// that edit drift on the entry-NULL edge only — a filter nuance, never a
// count the team reads.
export const CARE_ENTRY_STAGE_SLUGS: ReadonlySet<string> = new Set(
  Object.entries(TEMPLATES)
    .filter(([key]) => key.includes(":ongoing_care:"))
    .map(([, stages]) => stages[0]?.slug)
    .filter((slug): slug is string => Boolean(slug)),
);

/**
 * The name a stage slug reads as, for a one-line summary of a record (a
 * search result, the "Also happening" list), the Nov 3 re-check's item 3:
 * those printed the raw slug ("carrier_issued", "application submitted").
 *
 * DECISION: read from the code's own stage templates, not the database's
 * board overrides. A one-line summary across many records cannot afford a
 * board lookup per row, and an owner's renamed stage is rare; when the
 * record's own company and type are known, that board's default wins,
 * otherwise the first template naming the slug, otherwise its Title Case.
 * The record's own page still reads the live board (resolveStageTemplate).
 */
export function stageLabelForSummary(
  slug: string,
  record?: { companySlug: string; workflow: Workflow; type: string },
): string {
  if (!slug) return "";
  if (record) {
    const own = lookupStageTemplate(record).stages.find((s) => s.slug === slug);
    if (own) return own.label;
  }
  for (const stages of Object.values(TEMPLATES)) {
    const found = stages.find((s) => s.slug === slug);
    if (found) return found.label;
  }
  return titleCaseSlug(slug);
}
